import React, { MouseEvent, ReactNode } from 'react';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomLoading from '../../custom/CustomLoading';
import ValueFactory from '../../../util/ValueFactory';
import { RootState } from '../../../redux/store';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ComponentName from '../../../constant/ComponentName';
import Breadcrumb from '../../Breadcrumb';
import CustomTabv2 from '../../custom/CustomTabv2';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import TransactionService from '../../../service/TransactionService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import LanguageUtil from '../../../util/LanguageUtil';
import { changeEvent, changeEventDependencies, changeEventDirty, changeEventIsUpdate, clearEvent } from '../../../redux/actions/ChannelActions';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import { clearPage } from '../../../redux/actions/PageActions';
import TransactionVariableListComponent from './TransactionVariableListComponent';
import TransactionAccessComponent from './TransactionAccessComponent';
import { ComponentDependency } from '../../../domain/ui/ComponentMetadata';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import RecordNotFound from '../../../pages/RecordNotFound';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import eq = query.eq;
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomConfirmDialog from '../../custom/CustomConfirmDialog';
import ChannelUndeployImpactDialog from '../ChannelUndeployImpactDialog';
import { ChannelUndeployDependent } from '../../../domain/stream/transaction/ChannelUndeployDependent';

class TransactionEditorContainer extends StandardEditorContainerComponent<Props, State, TransactionJsonModel> {
  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.TRANSACTION,
      icon: 'transaction',
    };
    super(props, args);
  }

  public state: State = new State();

  private channelSubscription: subscribe.Subscription | undefined;
  private dependencySubscription: subscribe.Subscription | undefined;
  private structSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly channelService: TransactionService = ServiceContainer.getInstance().get(ServiceNames.CHANNEL);

  public render(): ReactNode {
    const { loading } = this.state;
    const {
      event: { event, isUpdate },
    } = this.props;
    const { undeployDialogVisible, dependentDialogVisible, undeployDependents } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    if (this.props.router.params.id && event.name == '') {
      return <RecordNotFound itemId={this.props.router.params.id} component={ComponentName.TRANSACTION} />;
    }
    return (
      <div className={`flex flex-col h-full w-full overflow-y-hidden`}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomConfirmDialog
          visible={undeployDialogVisible}
          onHide={this.hideUndeployDialog}
          icon={<img src={'/icons/error.svg'} alt='error' className='w-[172px] h-[172px] flex items-center justify-center' />}
          title={LanguageUtil.get({
            id: 'messages.confirm.undeploy1',
            defaultMessage: 'Are you sure you want to undeploy?',
          })}
          description={LanguageUtil.get({
            id: 'messages.confirm.undeployDescription',
            defaultMessage: 'If you undeploy, all data belonging to this component will be deleted.',
          })}
          cancelLabel={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
          confirmLabel={LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          onConfirm={this.confirmUndeploy}
        />
        <ChannelUndeployImpactDialog
          visible={dependentDialogVisible}
          dependents={undeployDependents}
          onCancel={this.cancelDependentUndeploy}
          onConfirm={this.confirmDependentUndeploy}
        />
        <CustomEditorItemComponent
          name={event.name}
          description={event.description!}
          component={ComponentName.TRANSACTION}
          status={event?.stage ? event.stage : LanguageUtil.get({ id: 'common.status.draft', defaultMessage: 'Draft' })}
          onSave={this.onSave}
          onRemove={this.onRemove}
          onDeploy={this.onDeploy}
          onUndeploy={this.onUndeploy}
          isUpdate={isUpdate}
          icon={'transaction'}
          onChangeDescription={this.onChangeDescription}
          onChangeName={this.onChangeName}
          disableSave={!RoleHelper.canEdit('Transaction')}
          disableRemove={!RoleHelper.canEdit('Transaction')}
          disableNameChange={!RoleHelper.canEdit('Transaction')}
          disableDescriptionChange={!RoleHelper.canEdit('Transaction')}
          disableDeploy={!RoleHelper.canDeploy('Transaction')}
          disableUndeploy={!RoleHelper.canDeploy('Transaction')}
        />
        <div className={`flex flex-col h-full w-full py-3 bg-white rounded`}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount() {
    const { clearEvent } = this.props;
    clearEvent();
    await super.componentWillUnmount();
  }

  protected _startTracker = () => {
    const eventId = this.getItemIdFromRoute();
    if (eventId == undefined) {
      this.setState({ loading: false });
    }
    const eventQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', eventId)).build();
    const metadataQuery = query.QueryBuilder.select().from(CollectionNames.COMPONENT_DEPENDENCY).where(eq('meta.transaction', eventId)).build();
    this.channelSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, eventQuery);
    this.structSubscription = subscribe.Formica.subscribe(
      CollectionNames.DATA_STRUCTURES,
      query.QueryBuilder.select().from(CollectionNames.DATA_STRUCTURES).build(),
    );
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.channelSubscription]);
    if (eventId != undefined) {
      this.dependencySubscription = subscribe.Formica.subscribe(CollectionNames.COMPONENT_DEPENDENCY, metadataQuery);
      this.computation.addSubscription(this.dependencySubscription);
    }
  };

  protected _stopTracker = async () => {
    await this.channelSubscription?.stop();
    await this.dependencySubscription?.stop();
    await this.structSubscription?.stop();
    this.computation?.stop();
  };

  protected tracker = async () => {
    const { changeEventDirty } = this.props;
    if (this.channelSubscription?.ready() && (this.dependencySubscription == undefined || this.dependencySubscription?.ready())) {
      const eventId = this.getItemIdFromRoute();
      if (eventId == undefined) {
        this.changeIsUpdate(false);
        this.setState({ loading: false });
        RoleHelper.setCurrentAccess('Edit');
        changeEventDirty(false);
        return;
      }
      const eventQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', eventId)).build();
      const dependencyQuery = query.QueryBuilder.select().from(CollectionNames.COMPONENT_DEPENDENCY).where(eq('meta.transaction', eventId)).build();
      const event: TransactionJsonModel = (await this.repository.find(eventQuery)) || ValueFactory.initializeEvent();
      const dependencies = await this.repository.findMany<ComponentDependency>(dependencyQuery);
      this.changeItem(event);
      this.changeIsUpdate(true);
      this.changeDependencies(dependencies);
      RoleHelper.setCurrentAccess(event);
      changeEventDirty(false);
      this.setState({ loading: false });
    }
  };

  private changeIsUpdate = (value: boolean) => {
    const { changeEventIsUpdate } = this.props;
    changeEventIsUpdate(value);
  };

  private changeDependencies = (dependencies: ComponentDependency[]) => {
    const { changeEventDependencies } = this.props;
    changeEventDependencies(dependencies);
  };

  private switchToUpdate = async () => {
    const {
      event: { event },
      router: { navigate, params },
      enterComponent,
    } = this.props;
    if (event.name == '') return;
    enterComponent({ component: ComponentName.TRANSACTION, id: event.name });
    navigate(`/transaction${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${event.name}`, { replace: true });
    setTimeout(() => {
      this.computation?.onStop(() => this.startTracker());
      this.stopTracker();
    });
  };

  private onChangeName = (name: string) => {
    const {
      event: { event },
    } = this.props;
    const value = name.trim();
    if (isStringValid(value)) {
      event.name = value;
      this.changeItem(event);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      event: { event },
    } = this.props;
    event.description = description;
    this.changeItem(event);
  };

  private onSave = async () => {
    const {
      event: { event },
      isUpdate,
      lastCategory,
      changeEventDirty,
    } = this.props;
    try {
      const isSaved = await this.channelService.saveEvent(event, isUpdate, lastCategory);
      if (isSaved) {
        changeEventDirty(false);
        this.changeIsUpdate(true);
        if (!isUpdate) await this.switchToUpdate();
      }
    } catch (e) {
      console.error(e);
    }
  };

  private onRemove = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      event: { event },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.channelService.removeEvent(event);
        if (success) {
          navigate(`/transaction`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeTransaction', defaultMessage: 'Are you sure to remove this transaction ?' }),
    });
  };

  private onDeploy = (e: any) => {
    const {
      event: { event },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.channelService.saveEvent(event, true, {} as EnrichedComponentCategory);
        await this.channelService.deployEvent(event);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deployTransaction', defaultMessage: 'Are you sure to deploy this transaction ?' }),
    });
  };

  private onUndeploy = async (e: any) => {
    e?.preventDefault?.();
    this.setState({ undeployDialogVisible: true });
  };

  private hideUndeployDialog = () => {
    this.setState({ undeployDialogVisible: false, dependentDialogVisible: false, undeployDependents: [] });
  };

  private cancelDependentUndeploy = () => {
    this.hideUndeployDialog();
  };

  private confirmUndeploy = async () => {
    const {
      event: { event },
    } = this.props;
    const dependents = await this.loadUndeployDependents(event.name);
    if (dependents == undefined) {
      return;
    }
    if (dependents.length === 0) {
      await this.channelService.unDeployEvent(event);
      this.hideUndeployDialog();
      return;
    }
    this.setState({ undeployDialogVisible: false, dependentDialogVisible: true, undeployDependents: dependents });
  };

  private confirmDependentUndeploy = async () => {
    const {
      event: { event },
    } = this.props;
    await this.channelService.unDeployEvent(event);
    this.hideUndeployDialog();
  };

  private loadUndeployDependents = async (id: string): Promise<ChannelUndeployDependent[] | undefined> => {
    if (id == undefined || id.trim().length === 0) {
      return [];
    }
    try {
      return await this.channelService.listUndeployDependents(id);
    } catch {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' }),
        detail: LanguageUtil.get({
          id: 'messages.error.undeployDependentsCheckFailed',
          defaultMessage: 'Bound components could not be listed. Undeploy was cancelled.',
        }),
      });
      return undefined;
    }
  };

  protected getTabs = () => {
    return [
      {
        key: 'Variable',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        content: <TransactionVariableListComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <TransactionAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.TRANSACTION, true),
        testid: `component_access_tab`,
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.TRANSACTION} documentName={this.props.event.event.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.TRANSACTION, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: TransactionJsonModel) => {
    const { changeEvent, changeEventDirty } = this.props;
    changeEvent(item);
    changeEventDirty(true);
  };
}

class State {
  loading: boolean = true;
  undeployDialogVisible: boolean = false;
  dependentDialogVisible: boolean = false;
  undeployDependents: ChannelUndeployDependent[] = [];
}

interface Props extends FormicaProps, ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  lastCategory: state.page.lastCategory,
  event: state.event,
  isUpdate: state.event.isUpdate,
  auth: state.auth,
});
const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeEvent,

  changeEventIsUpdate,
  changeEventDependencies,
  clearEvent,
  clearPage,
  changeEventDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(TransactionEditorContainer));
